import React from "react";
import {
  Box,
  Typography,
  Alert,
  Button,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  AddRounded,
  ArrowDropDown as ArrowDropDownIcon,
  BookmarksOutlined,
  Refresh as RefreshIcon,
  SwapVertRounded,
  TuneRounded,
} from "@mui/icons-material";
import { useDashboardWatchlist } from "../../../hooks/useDashboardWatchlist";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { useWatchlistTabs } from "../Watchlists/hooks/useWatchlistTabs";
import { useWatchlistViewPrefs } from "../Watchlists/hooks/useWatchlistViewPrefs";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import ManageWatchlistModal from "../Watchlists/ManageWatchlistModal";
import WatchlistSwitcher from "../Watchlists/WatchlistSwitcher";
import WatchlistNameModal from "../Watchlists/WatchlistNameModal";
import WatchlistSortMenu from "../Watchlists/WatchlistSortMenu";
import { sortWatchlistEntries } from "../Watchlists/sortWatchlistEntries";
import { MAX_WATCHLISTS_PER_USER } from "../Watchlists/watchlistLimits";
import CompanyPulseCard, { PulseStock } from "../CompanyPulseCard";
import { hapticSelection } from "../../../lib/haptics";
import { panelBorder, panelSurface, panelTitleSx, sectionHeaderSx } from "../overviewPanel";
import { touchHitAreaSx } from "../EarningsCalendar/touchHitArea";

// Used when the incoming row count isn't known ahead of time.
const DEFAULT_SKELETON_ROWS = 5;

// The user's watchlists, and ONLY those — a deliberately distinct module
// (Ben, 2026-08-27). Trending / Top Gainers / thematic lists were split back
// out of this strip: market discovery lives elsewhere (the Radar themes page;
// MarketMoversSection remains the standalone movers component awaiting its
// placement), so this section stays purely "my companies".

// Matches the 28px tab height so the controls sit flush with the tab strip
// on the section's header line.
const ICON_BUTTON_SIZE = 28;
const iconButtonSx = (isDark: boolean, idleColor: string) => ({
  width: ICON_BUTTON_SIZE,
  height: ICON_BUTTON_SIZE,
  color: idleColor,
  transition: "color 0.18s ease, background-color 0.18s ease",
  "&:hover": {
    color: "#22c55e",
    backgroundColor: isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.05)",
  },
  "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: -2 },
});

const CompactMarketMovers: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  const [activeSlot, setActiveSlot] = React.useState(0);
  // Which watchlist tab the dropdown was opened from, so selecting a list
  // reassigns that tab rather than some other one.
  const [switcher, setSwitcher] = React.useState<{
    anchorEl: HTMLElement;
    slotIndex: number;
  } | null>(null);
  const [isManageOpen, setIsManageOpen] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = React.useState<HTMLElement | null>(null);
  const { trackFeature } = useAnalytics();

  const {
    watchlists,
    slots,
    assignSlot,
    selectWatchlist,
    isLoading: watchlistsLoading,
  } = useWatchlistTabs();

  // One tab per pinned watchlist (up to 3). A user with no watchlists still
  // gets one placeholder tab, so the strip never collapses to nothing.
  const watchlistTabCount = Math.max(slots.length, 1);
  // Deleting a watchlist shortens the strip; clamping keeps the selection on
  // a tab that exists.
  const activeTab = Math.min(activeSlot, watchlistTabCount - 1);
  const activeWatchlist = slots[activeTab] ?? null;

  const {
    data: watchlistData,
    isLoading: watchlistLoading,
    error: watchlistError,
    refetch: refetchWatchlist,
  } = useDashboardWatchlist(activeWatchlist?.id ?? null, {
    // Until the pinned lists resolve there is no id to scope by — fetching
    // then would hit the every-list union and immediately refetch scoped.
    enabled: !watchlistsLoading && activeWatchlist != null,
  });

  // The list's per-watchlist sort + display metric. Hydrated SYNCHRONOUSLY
  // from localStorage (like the tab slots above), so the order is known
  // before the fetch resolves — the first paint of the list is already in
  // the chosen order, with no default-then-resort jump and no second fetch
  // (sorting is client-side; the API returns every field regardless).
  const { sort, dir, show, setSortPref, setShowPref } = useWatchlistViewPrefs(
    activeWatchlist?.id ?? null,
  );

  // The order is LATCHED (the Radar SectorsTab rule): it re-derives when the
  // user acts — sort change, tab switch — or when membership changes, never
  // on a 10-second price tick. A % sort would otherwise shuffle near-tied
  // cards under the reader every poll. Values on the cards stay live; only
  // POSITION holds still. The ref write inside the memo is idempotent
  // memoization, deliberate as in SectorsTab.
  const latchRef = React.useRef<{
    watchlistId: string | null;
    sort: string;
    dir: string;
    order: string[];
  } | null>(null);
  const orderedEntries = React.useMemo(() => {
    const entries = watchlistData?.data;
    if (!entries || entries.length === 0) return [];
    const latch = latchRef.current;
    const latchHolds =
      latch !== null &&
      latch.watchlistId === (activeWatchlist?.id ?? null) &&
      latch.sort === sort &&
      latch.dir === dir &&
      latch.order.length === entries.length &&
      entries.every((entry) => latch.order.includes(entry.ticker));
    if (latchHolds) {
      const position = new Map(latch.order.map((ticker, index) => [ticker, index]));
      return [...entries].sort(
        (a, b) => (position.get(a.ticker) ?? 0) - (position.get(b.ticker) ?? 0),
      );
    }
    const sorted = sortWatchlistEntries(entries, sort, dir);
    latchRef.current = {
      watchlistId: activeWatchlist?.id ?? null,
      sort,
      dir,
      order: sorted.map((entry) => entry.ticker),
    };
    return sorted;
  }, [watchlistData, activeWatchlist?.id, sort, dir]);

  // Memoised so the 10-second price poll only produces new row objects when
  // the response itself changed — otherwise every card (each carrying a
  // MiniChart) re-renders on every tick regardless of whether anything moved.
  const watchlistStocks: PulseStock[] = React.useMemo(
    () =>
      orderedEntries.map((entry) => ({
        symbol: entry.ticker,
        name: entry.companyName,
        price: entry.price,
        changePct: entry.changePercentage,
        change: entry.change,
        marketCap: entry.marketCap,
        exchange: entry.exchange,
      })),
    [orderedEntries],
  );

  const isLoading = watchlistLoading || watchlistsLoading;
  const showList = !isLoading && !watchlistError && watchlistStocks.length > 0;

  // The API answers 503 when every quote in the batch failed; a 200 that
  // carries holdings but no rows is the same outage seen through the older
  // shape. Neither is an empty list, so neither gets the "Add stocks" pitch.
  const pricesUnavailable =
    !isLoading &&
    !watchlistError &&
    (watchlistData?.meta.total ?? 0) > 0 &&
    (watchlistData?.data.length ?? 0) === 0;
  const loadFailed = !isLoading && (!!watchlistError || pricesUnavailable);

  // One card per company, floating on the page — the same rhythm as the
  // news column beside it.
  const listSx = { display: "flex", flexDirection: "column", gap: 1.25 } as const;

  const hasNoWatchlists = !watchlistsLoading && watchlists.length === 0;
  // The exact row count is already known from the cached watchlists query, so
  // the skeleton reserves the real height and the list lands without shifting
  // anything.
  const skeletonRows = Math.max(
    activeWatchlist?.holdings_count ?? DEFAULT_SKELETON_ROWS,
    1,
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    hapticSelection();
    setActiveSlot(newValue);
    // Keep the app-wide default (what the ticker page adds to) on the list
    // the user is actually looking at.
    const watchlist = slots[newValue];
    if (watchlist) selectWatchlist(watchlist.id);
  };

  const handleWatchlistTabClick = (
    event: React.MouseEvent<HTMLElement>,
    slotIndex: number,
  ) => {
    // First click just selects the tab; clicking the active one opens the
    // sheet.
    //
    // On touch it opens whatever the watchlist count is. The old `> 1` guard
    // assumed the sheet only swapped between lists, so with one list there was
    // nothing to show — but it now carries create and manage too, and gating
    // it on having two watchlists would leave a user with one no way to make
    // a second.
    if (activeTab === slotIndex && (isTouch || watchlists.length > 1)) {
      hapticSelection();
      setSwitcher({ anchorEl: event.currentTarget, slotIndex });
    }
  };

  const handleSelectWatchlist = (watchlistId: string) => {
    if (switcher) assignSlot(switcher.slotIndex, watchlistId);
    selectWatchlist(watchlistId);
  };

  const handleDeleted = () => {
    // Fall back to the derived default rather than pointing at a dead id.
    selectWatchlist(null);
    setActiveSlot(0);
  };

  const activeWatchlistLabel = activeWatchlist?.name ?? "Your Watchlist";
  const atWatchlistLimit = watchlists.length >= MAX_WATCHLISTS_PER_USER;

  const handleSortChange = (nextSort: typeof sort, nextDir: typeof dir) => {
    setSortPref(nextSort, nextDir);
    trackFeature("watchlist_sort_changed", { sort: nextSort, dir: nextDir });
  };

  const handleShowChange = (nextShow: typeof show) => {
    setShowPref(nextShow);
    trackFeature("watchlist_show_changed", { show: nextShow });
  };

  // The sort control rides the header on BOTH pointer and touch (Ben,
  // 2026-09-01) — a deliberate exception to the touch rule below: one
  // view-control icon beside the strip it re-orders is not the unattached
  // two-action row that failed; create/manage stay in the sheet on touch.
  const sortControl = activeWatchlist && (
    <Tooltip title="Sort & display">
      <IconButton
        size="small"
        onClick={(event) => {
          hapticSelection();
          setSortMenuAnchor(event.currentTarget);
        }}
        aria-label="Sort & display"
        aria-haspopup="menu"
        aria-expanded={!!sortMenuAnchor}
        sx={{
          ...iconButtonSx(isDark, theme.palette.text.secondary),
          // 28px to look at, 44px tall to hit on touch
          ...(isTouch && touchHitAreaSx(ICON_BUTTON_SIZE)),
        }}
      >
        <SwapVertRounded sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );

  // Rendered either inline with the tabs (pointer) or on their own row above
  // them (touch), so the markup lives once.
  const watchlistControls = (
    <>
      <Tooltip
        title={
          atWatchlistLimit
            ? `Limit of ${MAX_WATCHLISTS_PER_USER} watchlists reached`
            : "New watchlist"
        }
      >
        {/* Disabled buttons swallow pointer events, so the span carries the hover */}
        <span>
          <IconButton
            size="small"
            onClick={() => setIsCreateOpen(true)}
            disabled={atWatchlistLimit}
            aria-label="New watchlist"
            sx={iconButtonSx(isDark, theme.palette.text.secondary)}
          >
            <AddRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>

      {activeWatchlist && (
        <Tooltip title={`Manage ${activeWatchlistLabel}`}>
          <IconButton
            size="small"
            onClick={() => setIsManageOpen(true)}
            aria-label={`Manage ${activeWatchlistLabel}`}
            sx={iconButtonSx(isDark, theme.palette.text.secondary)}
          >
            <TuneRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* The header floats on the page in the band's title idiom — green
          mark, uppercase letterspaced label — with no rail ruling it off
          from the cards below. The watchlist names ARE the title: the
          active list reads as the section's label, its siblings sit beside
          it as quiet text, so there are no pill buttons to carry.
          No control row on touch. Both attempts at one failed for the same
          reason (Ben, 2026-07-31): two bare icons above the tab strip belong
          to nothing visually, whichever edge they are pushed to. They live
          inside the watchlist sheet, which is the surface the user already
          opens to think about watchlists. Pointer keeps them inline with the
          tabs, where a hover target beside its label is idiomatic. The lone
          sort icon is the recorded exception (Ben, 2026-09-01) — see
          sortControl above. */}
      <Box sx={sectionHeaderSx}>
        <BookmarksOutlined sx={{ fontSize: 15, color: "#22c55e", flexShrink: 0 }} />
        {/* Tab row — each label doubles as the watchlist switcher */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 28,
            flex: 1,
            minWidth: 0,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: 1 },
            "& .MuiTab-root": {
              ...panelTitleSx,
              color: theme.palette.text.secondary,
              py: 0,
              px: 0.5,
              minHeight: 28,
              minWidth: "auto",
              transition: "color 200ms linear",
              "&.Mui-selected": { color: "#22c55e" },
              "&:hover": { color: theme.palette.text.primary },
              "&.Mui-selected:hover": { color: "#22c55e" },
            },
          }}
        >
          {/* One tab per pinned watchlist — up to three, so the lists a user
              actually follows are one click away rather than two. */}
          {slots.map((watchlist, slotIndex) => (
            // Keyed by slot, not by watchlist id: the same watchlist may
            // legitimately be pinned to two tabs, and keying by id would then
            // produce duplicate sibling keys.
            <Tab
              key={`slot-${slotIndex}`}
              onClick={(event) => handleWatchlistTabClick(event, slotIndex)}
              aria-haspopup={watchlists.length > 1 ? "menu" : undefined}
              aria-expanded={switcher?.slotIndex === slotIndex}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    maxWidth: slots.length > 1 ? 116 : 160,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {watchlist.name}
                  </Box>
                  {/* Mirrors the open condition above: on touch the pill
                      always opens something, so it must always look like it
                      does. */}
                  {(isTouch || watchlists.length > 1) && (
                    <ArrowDropDownIcon
                      sx={{ fontSize: 16, flexShrink: 0, ml: -0.25 }}
                    />
                  )}
                </Box>
              }
            />
          ))}
          {/* Rendered when the user has no watchlists at all, so the strip
              never collapses to nothing. */}
          {slots.length === 0 && <Tab label="Your Watchlist" />}
        </Tabs>

        {/* Sort on both layouts; create/manage inline on pointer only. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            flexShrink: 0,
          }}
        >
          {sortControl}
          {!isTouch && watchlistControls}
        </Box>
      </Box>

      {/* Loading — card-shaped skeletons on the same rhythm, so nothing
          reflows */}
      {isLoading && (
        <Box sx={listSx}>
          {Array.from({ length: skeletonRows }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              animation="wave"
              height={86}
              sx={{
                borderRadius: 1.5,
                bgcolor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)",
              }}
            />
          ))}
        </Box>
      )}

      {loadFailed && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => refetchWatchlist()}
              sx={{ textTransform: "none", fontWeight: 600, color: "inherit" }}
            >
              Retry
            </Button>
          }
          sx={{
            borderRadius: 1.5,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.2)",
          }}
        >
          {!!watchlistError && "Couldn't load these stocks"}
          {!watchlistError && "Prices unavailable right now"}
        </Alert>
      )}

      {/* Empty: the user has no watchlists at all */}
      {!isLoading && !watchlistError && !pricesUnavailable && hasNoWatchlists && (
        <EmptyState
          title="You don't have a watchlist yet"
          body="Create one to track the companies you care about."
          actionLabel="Create watchlist"
          actionIcon={<AddRounded />}
          onAction={() => setIsCreateOpen(true)}
        />
      )}

      {/* Empty: a watchlist is selected but has no stocks */}
      {!isLoading &&
        !watchlistError &&
        !pricesUnavailable &&
        !hasNoWatchlists &&
        watchlistStocks.length === 0 && (
          <EmptyState
            title={`${activeWatchlistLabel} is empty`}
            body="Add stocks to follow their prices here."
            actionLabel="Add stocks"
            actionIcon={<AddRounded />}
            onAction={() => setIsManageOpen(true)}
          />
        )}

      {showList && (
        // Nothing here reacts to a background refetch: only the price and
        // chart actually change, and CompanyPulseCard flashes those
        // green/red itself.
        <Box sx={listSx}>
          {watchlistStocks.map((stock) => (
            <CompanyPulseCard
              key={stock.symbol}
              stock={stock}
              showChart
              size="compact"
              show={show}
            />
          ))}
        </Box>
      )}

      <WatchlistSortMenu
        open={!!sortMenuAnchor}
        anchorEl={sortMenuAnchor}
        onClose={() => setSortMenuAnchor(null)}
        sort={sort}
        dir={dir}
        show={show}
        onSortChange={handleSortChange}
        onShowChange={handleShowChange}
      />

      <WatchlistSwitcher
        open={!!switcher}
        anchorEl={switcher?.anchorEl ?? null}
        onClose={() => setSwitcher(null)}
        watchlists={watchlists}
        selectedWatchlistId={activeWatchlist?.id ?? null}
        onSelect={handleSelectWatchlist}
        // Touch only — the switcher ignores these on pointer, where the same
        // actions are the icon buttons beside the tabs.
        onCreate={() => setIsCreateOpen(true)}
        onManage={activeWatchlist ? () => setIsManageOpen(true) : undefined}
        createDisabledReason={
          atWatchlistLimit
            ? `Limit of ${MAX_WATCHLISTS_PER_USER} watchlists reached`
            : undefined
        }
        manageLabel={activeWatchlist?.name}
      />

      <WatchlistNameModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(created) => selectWatchlist(created.id)}
      />

      <ManageWatchlistModal
        open={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        watchlistId={activeWatchlist?.id ?? null}
        onDeleted={handleDeleted}
      />
    </Box>
  );
};

interface EmptyStateProps {
  title: string;
  body: string;
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  onAction?: () => void;
}

// Kept at the same minHeight as a populated list so the layout never jumps
// between the empty, loading and populated states. With no panel around the
// section any more, the empty state carries its own quiet card so it isn't
// just words floating on the page.
const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  body,
  actionLabel,
  actionIcon,
  onAction,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        minHeight: 240,
        px: 3,
        py: 4,
        borderRadius: 1.5,
        backgroundColor: panelSurface(isDark),
        border: `1px solid ${panelBorder(isDark)}`,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9375rem",
          color: theme.palette.text.primary,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: "0.8125rem",
          color: theme.palette.text.secondary,
          maxWidth: 280,
        }}
      >
        {body}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="contained"
          disableElevation
          startIcon={actionIcon}
          onClick={onAction}
          sx={{
            mt: 2.5,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            backgroundColor: "#22c55e",
            color: "#ffffff",
            "&:hover": { backgroundColor: "#16a34a" },
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default CompactMarketMovers;
